import type { GUID } from "@pdfking/tfunc"
import { CreateSingleton, GuidToString, Partial, UUID } from "@pdfking/tfunc"

import type { Hook, ImpositionSchema } from "#internal"
import type { ImposeResult } from "#public"

import { CreateSheets } from "../common/CreateSheets"
import { MediaSlots } from "../common/MediaSlots"
import { BuildColorMap } from "./data/BuildColorMap"
import { ApplyContextHooks, ApplyMediaHooks } from "./hooks/ApplyHooks"
import { BuildInformations, BuildSheetMath } from "./pipeline/BuildInformations"
import { BuildMeta3D } from "./pipeline/BuildMeta3D"
import { ComputeImpositionResult } from "./pipeline/ComputeImpositionResult"
import { PipelineSheets, ResolveSheet } from "./pipeline/PipelineSheets"

export type ImposeOptions = {
  /** The preset's own hooks: they run after the job's markings. */
  hooks?: Hook[]
  /** Fixed only where the output has to be reproducible (goldens); minted
   * otherwise. */
  job_id?: GUID
}

/**
 * Impose a validated spec: the press sheets, the pages they print, which pages
 * each destination prints, and what the numbering knows of the book. Nothing
 * here knows about files.
 */
export const Impose = (
  spec: ImpositionSchema,
  { hooks: preset_hooks = [], job_id = UUID() }: ImposeOptions = {}
): ImposeResult => {
  const hooks = [...spec.marking, ...preset_hooks]
  const {
    numbering,
    processors,
    sheets: packed,
    fills,
    is_book,
  } = PipelineSheets(spec, job_id, hooks)
  const { pages, destinations, sheet_destinations, sheet_divisions } = ComputeImpositionResult(
    packed.map(sheet => sheet.info),
    numbering
  )
  const media_processor = ApplyMediaHooks(processors)
  const context_processor = ApplyContextHooks(processors)

  return {
    job_id: GuidToString(job_id),
    input: spec,
    sheets: CreateSheets(
      packed.map((sheet, i) => ({
        frame: sheet.info.position,
        bleed: sheet.info.bleed,
        destinations: sheet_destinations[i]!,
        divisions: sheet_divisions[i]!,
        repetition_number: sheet.info.repetition_number,
        layout: Partial(ResolveSheet, sheet),
        ctx: {
          side: sheet.info.side,
          page_number_of: numbering.page_number_of,
          page_informations: numbering.informations,
          bleed: spec.bleed,
          media_processor,
          context_processor,
        },
      })),
      MediaSlots(destinations)
    ),
    pages,
    destinations,
    book: is_book ? numbering.informations : undefined,
    fills,
    book3d: CreateSingleton(Partial(BuildMeta3D, spec, numbering, is_book)),
    color_map: BuildColorMap(hooks),
    ...BuildInformations(spec, numbering, is_book),
    sheet_math: BuildSheetMath(spec, fills),
  }
}
